import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useTerritories } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    Procedure,
    IUseQueryPaged,
    QueryHookParams,
    QueryType,
    ProcessingDashboardData,
    QueryResult,
    QueryParams,
    MutationResult,
    CreateProcedureRequest,
    FinalizeProcedureRequest,
    PostProcedureEval,
    Invoice,
} from "../models";
import { Operation } from "rfc6902";

interface UseProcedureQueryParams extends Omit<QueryHookParams, "queryType"> {
    queryType?: QueryType | "postProcedureEval" | "processingDashboard" | "invoice";
    caseId?: number | string;
    tissueId?: number | string;
}

interface IUseProcedureQuery
    extends Omit<IUseQueryPaged<Procedure>, "createMutation" | "pagedListQuery" | "listQuery"> {
    createMutation: MutationResult<Procedure, unknown, CreateProcedureRequest, Procedure>;
    upsertMutation: MutationResult<Procedure, unknown, Procedure>;
    unlockMutation: MutationResult<Procedure, unknown, void>;
    processingDashboardQuery: QueryResult<ProcessingDashboardData>;
    finalizeMutation: MutationResult<void, unknown, FinalizeProcedureRequest>;
    postProcedureEvalQuery: QueryResult<PostProcedureEval>;
    invoiceQuery: QueryResult<Invoice | undefined>;
}

export const useProcedureQuery = (params?: UseProcedureQueryParams): IUseProcedureQuery => {
    const procedureId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const caseId = isNaN(Number(params?.caseId)) ? 0 : Number(params?.caseId);
    const tissueId = isNaN(Number(params?.tissueId)) ? 0 : Number(params?.tissueId);
    const { isAuthorized } = useAuth();
    const { get, post, put, patch, del } = useAxios();
    const { selectedOfficeIds } = useTerritories();
    const url = Endpoints.Procedures;

    const queryType = useMemo(() => {
        if (params?.queryType === "postProcedureEval") return "postProcedureEval";
        if (params?.queryType === "processingDashboard") return "processingDashboard";
        if (params?.queryType === "invoice") return "invoice";
        return getQueryType(procedureId, params?.queryType);
    }, [params?.queryType, procedureId]);

    const queryParams: QueryParams = {
        officeIds: selectedOfficeIds.join(","),
    };

    const processingDashboardQuery = useGenericQuery({
        queryFn: (config) =>
            get<ProcessingDashboardData>(getRequestOptions(`${url}/processing-dashboard`, config, queryParams)),
        queryKey: queryKeys.dashboards.processing(queryParams),
        placeholderData: {} as ProcessingDashboardData,
        enabled: isAuthorized && queryType === "processingDashboard",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Procedure>({ url: `${url}/${procedureId}`, config }),
        queryKey: queryKeys.procedures.detail(procedureId),
        placeholderData: undefined,
        enabled: isAuthorized && !!params?.id,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const postProcedureEvalQuery = useGenericQuery({
        queryFn: (config) =>
            get<PostProcedureEval>({
                url: `${Endpoints.Tissues}/${tissueId}/procedures/${procedureId}/post-procedure-eval`,
                config,
            }),
        queryKey: queryKeys.procedures.postProcedureEval(procedureId, tissueId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === "postProcedureEval" && !!tissueId && !!procedureId,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const invoiceQuery = useGenericQuery({
        queryFn: (config) => get<Invoice | undefined>({ url: `${url}/${procedureId}/invoice`, config }),
        queryKey: queryKeys.procedures.invoice(procedureId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === "invoice" && !!procedureId,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: CreateProcedureRequest) => post<CreateProcedureRequest, Procedure>({ url, data }),
        queryKey: queryKeys.procedures.all(),
        keysToInvalidate: [
            queryKeys.cases.detail(caseId),
            queryKeys.cases.detailDeep(caseId),
            queryKeys.tissues.detail(tissueId),
        ],
        successToast: "Procedure created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], Procedure>({ url: `${url}/${procedureId}`, data }),
        queryKey: queryKeys.procedures.detail(procedureId),
        keysToInvalidate: [
            queryKeys.cases.detail(caseId),
            queryKeys.cases.detailDeep(caseId),
            queryKeys.tissues.detail(tissueId),
        ],
        successToast: "Procedure updated",
    });

    const upsertMutation = useGenericMutation({
        mutationFn: (data: Procedure) => put<Procedure>({ url, data }),
        queryKey: queryKeys.procedures.detail(procedureId),
        keysToInvalidate: [
            queryKeys.cases.detail(caseId),
            queryKeys.cases.detailDeep(caseId),
            queryKeys.tissues.detail(tissueId),
        ],
        disableSuccessToast: true,
    });

    const unlockMutation = useGenericMutation({
        mutationFn: () => put<void, Procedure>({ url: `${url}/${procedureId}/unlock` }),
        queryKey: queryKeys.procedures.detail(procedureId),
        keysToInvalidate: [],
        successToast: "Procedure unlocked",
    });

    const finalizeMutation = useGenericMutation({
        mutationFn: (data: FinalizeProcedureRequest) =>
            patch<FinalizeProcedureRequest, void>({ url: `${url}/finalize`, data }),
        queryKey: queryKeys.procedures.detail(procedureId),
        keysToInvalidate: [queryKeys.cases.all(), queryKeys.tissues.all(), queryKeys.lists.all()],
        successToast: "Procedure finalized",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${procedureId}` }),
        queryKey: queryKeys.procedures.detail(procedureId),
        keysToInvalidate: [
            queryKeys.cases.detail(caseId),
            queryKeys.cases.detailDeep(caseId),
            queryKeys.tissues.detail(tissueId),
        ],
        successToast: "Procedure deleted",
    });

    return useMemo<IUseProcedureQuery>(
        () => ({
            processingDashboardQuery,
            detailQuery,
            invoiceQuery,
            createMutation,
            updateMutation,
            upsertMutation,
            unlockMutation,
            finalizeMutation,
            deleteMutation,
            postProcedureEvalQuery,
        }),
        [
            processingDashboardQuery,
            detailQuery,
            invoiceQuery,
            createMutation,
            updateMutation,
            upsertMutation,
            unlockMutation,
            finalizeMutation,
            deleteMutation,
            postProcedureEvalQuery,
        ]
    );
};
